"use client";
import React, { useState } from "react";
import { iWidgetChatConfig } from "@/app/models/interfaceChatWidget";
import ChatWidgetPreview from "./ChatWidgetPreview";

interface Props {
  configStart: iWidgetChatConfig;
  onChange: (newConfig: iWidgetChatConfig) => void;
  onClose: () => void;
}

export default function ChatWidgetEditor({ configStart, onChange, onClose }: Props) {
  const [config, setConfig] = useState<iWidgetChatConfig>(configStart);
  const [activeTab, setActiveTab] =
    useState<"boton" | "iframe" | "tooltip">("boton");

  const handleChange = (path: string, newValue: string | number) => {
    const updated = structuredClone(config);
    const keys = path.split(".");
    let current: any = updated;
    for (let i = 0; i < keys.length - 1; i++) current = current[keys[i]];
    current[keys[keys.length - 1]] =
      typeof newValue === "number" ? `${newValue}px` : newValue;
    setConfig(updated);
    
  };

  const inputStyle =
    "border border-gray-300 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-600 transition-all shadow-sm w-full";

  const renderInput = (path: string, value: string) => {
    if (path.includes("fontWeight")) {
      const num = parseInt(value) || 400;
      return (
        <div className="flex flex-col space-y-1">
          <input
            type="range"
            min={100}
            max={900}
            step={100}
            value={num}
            onChange={(e) => handleChange(path, e.target.value)}
            className="w-full accent-blue-600"
          />
          <div className="flex justify-between text-xs text-gray-600">
            <span>{num}</span>
            <span>Ligera</span>
            <span>Negrita</span>
          </div>
        </div>
      );
    } 
    // px
    const match = /^(\d+)\s*px$/.exec(value);
    if (match) {
      const num = parseInt(match[1]);
      const isFontSize = path.includes("fontSize");
      const maxValue = isFontSize ? 50 : 500;

      return (
        <div className="flex flex-col space-y-1">
          <input
            type="range"
            min={0}
            max={maxValue}
            value={num}
            onChange={(e) => handleChange(path, Number(e.target.value))}
            className="w-full accent-blue-600"
          />
          <div className="flex justify-between text-xs text-gray-600">
            <span>{num}px</span>
            <span className="text-gray-500">
              {isFontSize ? "Máx. 50 px" : "Máx. 500 px"}
            </span>
          </div>
        </div>
      );
    }
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => handleChange(path, e.target.value)}
        className={inputStyle}
      />
    );
  };


  const colorInput = (path: string, value: string, label: string) => (
    <div className="flex flex-col w-full">
      <span className="text-sm text-gray-700 mb-1">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => handleChange(path, e.target.value)}
          className="h-10 w-12 border border-gray-300 rounded-lg cursor-pointer flex-shrink-0"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => handleChange(path, e.target.value)}
          className={`${inputStyle} flex-1`}
        />
      </div>
    </div>
  );

  return (
    <div className="p-6 bg-white rounded-2xl w-full min-w-[800px] max-w-[1600px] mx-auto">
      <h2 className="text-2xl font-semibold text-gray-800 mb-4 sm:mb-6 border-b pb-3">
        Configuración
      </h2>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-blue-200">
        {[
          { id: "boton", label: "Botón" },          
          { id: "tooltip", label: "Mensaje emergente" },
          { id: "iframe", label: "Cuadro de chat" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() =>
              setActiveTab(tab.id as "boton" | "iframe" | "tooltip")
            }
            className={`px-4 py-2 text-sm font-medium rounded-t-md transition-all ${
              activeTab === tab.id
                ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                : "text-gray-600 hover:text-blue-600"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenido */}
      <div className="bg-gray-50 p-5 rounded-b-xl border-b border-gray-200 shadow-sm">
        {activeTab === "boton" && (
          <section className="flex flex-col gap-4">
            {/* Texto del botón */}
            <label className="flex flex-col">
              <span className="text-sm text-gray-700 mb-1">Texto del botón</span>
              <input
                type="text"
                value={config.button.text}
                onChange={(e) => handleChange("button.text", e.target.value)}
                className={inputStyle}
              />
            </label>

            {/* Tamaño */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-1">Ancho</span>
                {renderInput("button.width", config.button.width)}
              </label>
              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-1">Alto</span>
                {renderInput("button.height", config.button.height)}
              </label>
            </div>

            {/* Colores */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {colorInput(
                "button.backgroundColor",
                config.button.backgroundColor,
                "Color de fondo"
              )}
              {colorInput("button.color", config.button.color, "Color del texto")}
            </div>

            {/* Bordes y fuente */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-1">Borde</span>
                {renderInput("button.borderRadius", config.button.borderRadius)}
              </label>
              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-1">Tamaño fuente</span>
                {renderInput("button.fontSize", config.button.fontSize)}
              </label>
              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-1">
                  Grosor de fuente
                </span>
                {renderInput("button.fontWeight", config.button.fontWeight)}
              </label>
            </div>

            {/* Ícono */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-1">Tamaño ícono</span>
                {renderInput("button.iconSize", config.button.iconSize)}
              </label>
              {colorInput(
                "button.iconColor",
                config.button.iconColor,
                "Color del ícono"
              )}
            </div>

            {/* SVG e icono */}
            <div className="flex flex-col gap-3">
              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-1">SVG del ícono</span>
                <div className="flex items-start gap-3">
                  <textarea
                    value={config.button.iconSvg}
                    onChange={(e) =>
                      handleChange("button.iconSvg", e.target.value)
                    }
                    className={`${inputStyle} font-mono text-xs min-h-[100px] flex-1`}
                  />
                  <div className="p-2 border border-gray-200 rounded-xl shadow-sm flex items-center justify-center w-[60px] h-[60px]">
                    {config.button.iconSvg?.trim() ? (
                      <div className="text-slate-400"
                        dangerouslySetInnerHTML={{
                          __html: config.button.iconSvg,
                        }}
                        style={{
                          width: 40,
                          height: 40,
                          color: "",
                        }}
                      />
                    ) : (
                      <span className="text-gray-400 text-xs">Sin ícono</span>
                    )}
                  </div>
                </div>
              </label>
            </div>

            {/* Posición */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-1">
                  Distancia inferior
                </span>
                {renderInput("button.position.bottom", config.button.position.bottom)}
              </label>
              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-1">
                  Distancia derecha
                </span>
                {renderInput("button.position.right", config.button.position.right)}
              </label>
            </div>
          </section>
        )}

        {activeTab === "iframe" && (
          <section className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-1">Ancho</span>
                {renderInput("iframe.width", config.iframe.width)}
              </label>
              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-1">Alto</span>
                {renderInput("iframe.height", config.iframe.height)}
              </label>
              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-1">
                  Radio del borde
                </span>
                {renderInput("iframe.borderRadius", config.iframe.borderRadius)}
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {colorInput(
                "iframe.borderColor",
                config.iframe.borderColor,
                "Color del borde"
              )}
              {colorInput(
                "iframe.backgroundColor",
                config.iframe.backgroundColor,
                "Color de fondo"
              )}
            </div>
          </section>
        )}

        {activeTab === "tooltip" && (
          <section className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-1">Texto</span>
                <input
                  type="text"
                  value={config.button.tooltip.text}
                  onChange={(e) =>
                    handleChange("button.tooltip.text", e.target.value)
                  }
                  className={inputStyle}
                />
              </label>
              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-1">Posición</span>
                <select
                  value={config.button.tooltip.position}
                  onChange={(e) =>
                    handleChange("button.tooltip.position", e.target.value)
                  }
                  className={`${inputStyle} cursor-pointer`}
                >
                  <option value="arriba">Arriba</option>
                  <option value="abajo">Abajo</option>
                  <option value="izquierdo">Izquierdo</option>
                  <option value="derecho">Derecho</option>
                </select>
              </label>


              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-1">
                  Ancho
                </span>
                {renderInput(
                  "button.tooltip.width",
                  config.button.tooltip.width
                )}
              </label>
              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-1">
                  Alto
                </span>
                {renderInput(
                  "button.tooltip.height",
                  config.button.tooltip.height
                )}
              </label>


               


              {colorInput(
                "button.tooltip.backgroundColor",
                config.button.tooltip.backgroundColor,
                "Color de fondo"
              )}
              {colorInput(
                "button.tooltip.color",
                config.button.tooltip.color,
                "Color del texto"
              )}

              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-1">
                  Tamaño de fuente
                </span>
                {renderInput(
                  "button.tooltip.fontSize",
                  config.button.tooltip.fontSize
                )}
              </label>

              <label className="flex flex-col">
                <span className="text-sm text-gray-700 mb-1">
                  Radio del borde
                </span>
                {renderInput(
                  "button.tooltip.borderRadius",
                  config.button.tooltip.borderRadius
                )}
              </label>
            </div>
          </section>
        )}
      </div>
      <div className="flex justify-end mt-4">
        <button
          onClick={onClose}
          className="btn-inca-secondary px-5 py-2 rounded-xl shadow-sm transition-all duration-200 mr-5"
        >
          Cerrar
        </button>
         <button
          onClick={()=>onChange(config)}
          className="n-buttons px-5 py-2 rounded-xl shadow-sm transition-all duration-200"
        >
          Guardar
        </button>
      </div>

            
        <ChatWidgetPreview config={config}  activeTab={activeTab}/>
      
    </div>
  );
}
